import React, { useState, useMemo, useRef, useCallback } from 'react'

import { Theme, useTheme } from '@mui/material/styles'

import 'chart.js/auto'
import { Chart } from 'react-chartjs-2'

import { useTranslation } from 'language/translator'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import ChartjsPluginStacked100 from 'chartjs-plugin-stacked100'

import Box from 'components_new/atoms/Box'

import {
  horizontalLinePlugin,
  boxBorderDashPlugin,
  verticalLinePlugin,
  forecastIsActive,
  Value
} from '../utils'

import { BarChartOptions, getBars, getOptions } from './utils'

import { translateWidgetType } from 'utils/enumTranslator'

import NoValue from '../../Errors/NoValue'
import SegmentMenu from '../../SegmentMenu'

import {
  FormattedWidgetData,
  isDashboardWidgetFullyLoaded,
  ParsedSegmentPath,
  ParsedWidgetDataLabel,
  TCoreWidgetFullyLoaded,
  WidgetType
} from 'types/GlobalWidget'

interface BarChartProps {
  customSegmentBy: string | null
  formattedData: FormattedWidgetData
  isComparativeButIllegal: boolean
  onSegmentClick?: (
    event: React.MouseEvent<HTMLElement, MouseEvent> | PointerEvent,
    segments: ParsedWidgetDataLabel[],
    datasetIndex?: number
  ) => void
  scaleFactor: number
  setCustomSegmentBy:
    | ((attributeOptionId: string, segment: ParsedSegmentPath[]) => void)
    | null
  setDetailsDialog: ((segment: ParsedSegmentPath[]) => void) | null
  setSegmentFilters: ((segment: ParsedSegmentPath[]) => void) | null
  widget: TCoreWidgetFullyLoaded
}

type BarsType = {
  labels: string[]
  datasets: {
    label: string
    data: (string | number | null)[]
    theme: Theme
    increaseIsPositive: boolean
    percentages?: Value[]
  }[]
  dataLabels: ParsedWidgetDataLabel[]
}

const MemoizedChart = React.memo(
  ({
    accessibleName,
    allowOnClick,
    bars,
    chartRef,
    handleClick,
    horizontal,
    options
  }: {
    accessibleName: string
    allowOnClick: boolean
    bars: BarsType
    chartRef: any
    handleClick: (
      event: React.MouseEvent<HTMLElement, MouseEvent>,
      dataLabel: ParsedWidgetDataLabel,
      datasetIndex?: number
    ) => void
    horizontal: boolean
    options: BarChartOptions
  }) => (
    <Chart
      role="img"
      aria-label={accessibleName}
      ref={chartRef}
      type="bar"
      data={bars}
      options={options}
      plugins={[
        horizontalLinePlugin,
        ChartDataLabels,
        boxBorderDashPlugin,
        verticalLinePlugin,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        ChartjsPluginStacked100
      ]}
      onClick={
        allowOnClick
          ? (event) => {
              if (chartRef.current) {
                let index = null

                const { top } = chartRef.current.chartArea

                // If click is below legend
                if (event.nativeEvent.offsetY > top) {
                  if (horizontal) {
                    index = chartRef.current.scales.y.getValueForPixel(
                      event.nativeEvent.offsetY
                    )
                  } else {
                    index = chartRef.current.scales.x.getValueForPixel(
                      event.nativeEvent.offsetX
                    )
                  }

                  const segment = bars.dataLabels[index]

                  if (segment) {
                    const elements = chartRef.current.getElementsAtEventForMode(
                      event.nativeEvent,
                      'nearest',
                      { intersect: true },
                      false
                    )
                    const datasetIndex =
                      elements.length > 0 ? elements[0].datasetIndex : undefined

                    handleClick(event, segment, datasetIndex)
                  }
                }
              }
            }
          : undefined
      }
    />
  )
)

const BarChart = (props: BarChartProps) => {
  const { t } = useTranslation()
  const {
    customSegmentBy,
    formattedData,
    isComparativeButIllegal,
    onSegmentClick,
    scaleFactor,
    setCustomSegmentBy,
    setDetailsDialog,
    setSegmentFilters,
    widget
  } = props

  const chartRef = useRef<any>(null)
  const [anchorPosition, setAnchorPosition] = useState<{
    top: number
    left: number
  } | null>(null)
  const [selectedSegment, setSelectedSegment] =
    useState<ParsedWidgetDataLabel | null>(null)
  const allowOnClick = !!setSegmentFilters || !!setDetailsDialog

  const theme = useTheme()

  const hasBreakdown =
    formattedData.datasets.length > 1 ||
    !!widget.settings.kpi_options.find(
      (option) => !!option.breakdown_by.selected
    )

  const bars = useMemo(
    () => getBars(theme, hasBreakdown, formattedData, forecastIsActive(widget)),
    [hasBreakdown, formattedData, widget]
  )
  const options = useMemo(
    () =>
      getOptions(
        widget,
        hasBreakdown,
        theme,
        allowOnClick,
        formattedData,
        isComparativeButIllegal,
        scaleFactor
      ),
    [
      widget,
      hasBreakdown,
      allowOnClick,
      formattedData,
      isComparativeButIllegal,
      scaleFactor
    ]
  )

  const handleClick = useCallback(
    (
      event: React.MouseEvent<HTMLElement, MouseEvent>,
      dataLabel: ParsedWidgetDataLabel,
      datasetIndex?: number
    ) => {
      if (onSegmentClick) {
        onSegmentClick(event, [dataLabel], datasetIndex)

        return
      }

      event.stopPropagation()

      setAnchorPosition({ left: event.clientX, top: event.clientY })
      setSelectedSegment(dataLabel)
    },
    [onSegmentClick]
  )

  if (formattedData.datasets.length === 0) {
    return (
      <NoValue
        iconName={translateWidgetType[WidgetType.BAR_CHART].iconName}
        scaleFactor={scaleFactor}
      />
    )
  }

  const handleClose = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    event.stopPropagation()

    setAnchorPosition(null)
    setSelectedSegment(null)
  }

  return (
    <>
      <Box sx={{ position: 'relative', flexGrow: 1, minHeight: 0 }}>
        <MemoizedChart
          accessibleName={widget.title || t('global', 'chart')}
          allowOnClick={allowOnClick}
          bars={bars}
          chartRef={chartRef}
          handleClick={handleClick}
          horizontal={widget.settings.horizontal}
          options={options}
        />
      </Box>
      {allowOnClick &&
        selectedSegment &&
        isDashboardWidgetFullyLoaded(widget) && (
        <SegmentMenu
          anchorPosition={anchorPosition}
          customSegmentBy={customSegmentBy}
          dashboardId={widget.dashboard_id}
          onClose={handleClose}
          segments={[selectedSegment]}
          setCustomSegmentBy={setCustomSegmentBy}
          setDetailsDialog={setDetailsDialog}
          setFilters={setSegmentFilters}
          widget={widget}
        />
      )}
    </>
  )
}

export default BarChart
